/* Test component for embedding an iframe to the certificate validator
 * Has the purpose of testing communication between parent window and iframe
 */
import { useCallback, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { ROUTES } from "../../utils/routes";

export default function Test() {
    const VB_IDENTIFIER = "VB-CV1LP1ALL-9999";

    const [tries, setTries] = useState(1);

    const [placeholderPrefix, setPlaceholderPrefix] = useState(undefined);
    const [settings, setSettings] = useState([]);

    const toggleSettings = () => {
        if (settings.includes("no_input_header")) {
            setSettings(settings.filter((s) => s !== "no_input_header"));
        } else {
            setSettings([...settings, "no_input_header"]);
        }
    };
    // Count to identify tries to find the iframe and inject the credential-id, identifier and the language to the iframe src

    // Trusted origin for security reasons to call getContainerHeight function
    // var trustedOrigin = "$IFRAME_ORIGIN";

    // Event function to get the height of the iframe content and set the container height accordingly
    const getContainerHeight = useCallback((event) => {
        // Check the origin of the message for security reasons
        console.log("Message event origin:", event.origin);
        if (!event.origin) {
            return;
        }
        // As we work here from localhost, we do not need to check the origin. This component will never shiped to the clients.
        // var origin = new URL(event.origin).origin;
        // console.log("Received message from origin:", origin, "Origin is trusted:", origin === trustedOrigin);
        // if (origin !== trustedOrigin) {
        //     return;
        // }
        console.log("Message data is an Array:", Array.isArray(event.data));
        if (!Array.isArray(event.data)) {
            return;
        }
        console.log("Received message data:", event.data);
        var e = window.document.getElementById("certificate-validator-container");
        const [k, v] = event.data;
        if (e && v && k === "validatorContentRefHeight") {
            e.style.height = v.toString() + "px";
        }
    }, []);

    /**
     * Analyse URL params to not inject any malicious code.
     */
    function analyseUrlParams(param) {
        if (!param) return true;
        // Only allow alphanumeric, hyphen, underscore and german umlauts
        const regex = /^[a-zA-Z0-9-_äöüÄÖÜß]+$/;
        console.log("Analysing param:", param, "Valid:", regex.test(param));
        return regex.test(param);
    }

    /**
     * Apply parameters to the iframe src.
     * If the iFrame is not found, retry up to 100 times with a delay of 300ms.
     * If the iFrame is found, update its src with the identifier, default language and credential parameters if necessary.
     * @returns {void}
     */
    const applyParamsToIFrameSrc = useCallback(() => {
        var i = document.getElementById("certificate-validator-frame");
        console.log("Trying to apply params to iframe src, try number:", tries);
        if (!i || !window._vb_identifier) {
            // We retry to find the iframe up to 100 times (Total 30 seconds)
            if (tries <= 100) {
                setTries((prevTries) => prevTries + 1);
                setTimeout(applyParamsToIFrameSrc, 300);
                console.log(
                    "Iframe or identifier not found, retrying... - Iframe found:",
                    !!i,
                    "Identifier found:",
                    !!window._vb_identifier
                );
            }
            return;
        }
        if (!["de", "en"].includes(window._vb_identifier.default_lg)) {
            // default to English if invalid language is provided and prevent from security risks
            window._vb_identifier.default_lg = "en";
        }
        console.log("Found iframe and identifier, applying params to iframe src");
        var title = window._vb_identifier.default_lg === "de" ? "Zertifikatsprüfer" : "Certificate Validator";
        let src = i.getAttribute("src") || "";
        let urlSrc = new URL(src);
        // Read current applied params and check changes of credential, identifier or language
        const currentIFrameParams = urlSrc.searchParams;
        const parentUrlParams = new URLSearchParams(window.location.search);

        const haveNecessaryParams = currentIFrameParams.get("identifier") && currentIFrameParams.get("default_lg");

        if (
            haveNecessaryParams &&
            currentIFrameParams.get("identifier") === window._vb_identifier.identifier &&
            currentIFrameParams.get("default_lg") === window._vb_identifier.default_lg &&
            currentIFrameParams.get("credential") === parentUrlParams.get("credential")
        ) {
            // No changes, do not update the iframe src
            console.log("No changes in params, not updating iframe src");
            setTries(1); // reset tries
            return;
        }
        // Set title for accessibility
        i.title = title;

        const paramObject = { ...window._vb_identifier, credential: parentUrlParams.get("credential") || null };

        // Create an array of key-value pairs
        let paramArray = Object.entries(paramObject).map(([key, value]) => ({ key, value }));
        console.log("Params to apply to iframe src:", paramArray);

        // Update or add the params to the iframe src
        paramArray.forEach((param) => {
            // Only update if value is valid
            if (param.value && analyseUrlParams(param.value)) {
                // Update or add the param
                urlSrc.searchParams.set(param.key, param.value);
            } else if (param.key === "credential" && param.value === null) {
                // Remove the credential param if value is null
                urlSrc.searchParams.delete(param.key);
            }
        });

        if (!urlSrc.searchParams.get("identifier") || !urlSrc.searchParams.get("default_lg")) {
            console.log("Identifier or default_lg param is missing or invalid, retrying...");
            // If identifier or default_lg is missing, we need to retry
            if (tries <= 100) {
                setTries((prevTries) => prevTries + 1);
                setTimeout(applyParamsToIFrameSrc, 300);
            }
            return;
        }

        // Set the updated src to the iframe
        src = urlSrc.toString();
        i.src = src;

        setTries(1); // reset tries
        console.log("Updated iframe src to:", src);
    }, [tries]);

    /**
     * Observe the body for added nodes to detect when the iframe is added dynamically.
     * If the iframe is found, apply the parameters to its src.
     */
    const observe = useCallback(() => {
        var body = document.body;
        if (!body) {
            return;
        }

        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                mutation.addedNodes.forEach(function (node) {
                    // If the container is added dynamically
                    if (node.id === "certificate-validator-container") {
                        // Also check if iframe is already inside
                        var iframe = node.querySelector("#certificate-validator-frame");
                        if (iframe) {
                            console.log("Container and iframe added dynamically, applying params");
                            applyParamsToIFrameSrc();
                        }
                        return;
                    }

                    // If the iframe itself is added directly somewhere
                    if (node.id === "certificate-validator-frame") {
                        console.log("Iframe added dynamically, applying params");
                        applyParamsToIFrameSrc();
                    }
                });
            });
        });

        observer.observe(body, { childList: true, subtree: true });
    }, [applyParamsToIFrameSrc]);
    useEffect(() => {
        var timer = setTimeout(() => {
            // Comment out next line to trigger "network timeout"
            window._vb_identifier = { identifier: VB_IDENTIFIER, default_lg: "en" };
            return () => {
                clearTimeout(timer);
            };
        }, 3000);
        window.onload = function () {
            console.log("Window loaded, starting to observe for iframe");
            observe();
        };

        applyParamsToIFrameSrc();
        if (window.addEventListener) {
            window.addEventListener("message", getContainerHeight, false);
        } else if (window.attachEvent) {
            window.attachEvent("onmessage", getContainerHeight);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const params = () => {
        let params = [];
        if (settings.length) {
            params.push(`settings=${settings.join(",")}`);
        }
        if (placeholderPrefix) {
            params.push(`placeholder_prefix=${placeholderPrefix}`);
        }
        return params.length ? `?${params.join("&")}` : "";
    };

    useEffect(() => {
        applyParamsToIFrameSrc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);
    // cer-ba00f608-ab99-4c80-b1e6-a8e39bb3
    if (!window.location.hostname.includes("localhost")) return <Redirect to={ROUTES.notfound} />;
    return (
        <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
            This is a test page. The iframe will be below{" "}
            <div>
                <div>Toggle no input header (Hidden: {settings.includes("no_input_header") ? "Yes" : "No"})</div>
                <button onClick={toggleSettings}>Toggle</button>
            </div>
            <div>
                <div>Credential id prefix (Blur to apply)</div>
                <input type="text" onBlur={(e) => setPlaceholderPrefix(e.target.value)} />
            </div>
            {/* <button onClick={() => applyParamsToIFrameSrc()}>Apply to src</button> */}
            <div
                style={{ width: "100%", height: "480px", border: "1px solid #bb00ee" }}
                id="certificate-validator-container"
            >
                <iframe
                    id="certificate-validator-frame"
                    src={`http://localhost:3001/certificate-validator${params()}`}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Certificate Validator"
                />
            </div>
        </div>
    );
}
